<script setup>
// The metric's detail, for admins (phase 4.5, R2-4-5 = A: kept, in the kit's Sheet). The Sheet is the kit's modal
// drawer (reka Dialog): it traps and restores the focus and closes on Escape and on a click outside, which this
// component used to do by hand. Same data (useReportDrilldown on the assistant's drilldown endpoint), same states.
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import PolarisAssistant from 'dashboard/api/polaris/assistant';
import { useReportDrilldown } from 'dashboard/routes/dashboard/settings/reports/composables/useReportDrilldown';
import ReportDrilldownCard from 'dashboard/routes/dashboard/settings/reports/components/ReportDrilldownCard.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from 'dashboard/components-next/polaris/ui/sheet';

const props = defineProps({
  open: { type: Boolean, default: false },
  assistantId: { type: [String, Number], default: null },
  metric: { type: String, default: '' },
  metricName: { type: String, default: '' },
  metricValue: { type: String, default: '' },
  range: { type: String, default: '' },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const {
  records,
  meta,
  isFetching,
  isFetchingMore,
  hasError,
  hasRecords,
  hasMore,
  open: openDrilldown,
  close,
  loadMore,
} = useReportDrilldown(params => PolarisAssistant.getDrilldown(params));

const title = computed(() => props.metricName || '');

const subtitle = computed(() => {
  if (!meta.value.conversation_count) return '';

  return t('POLARIS.OVERVIEW.DRILLDOWN.RESULT_COUNT_CONVERSATION', {
    count: meta.value.conversation_count,
  });
});

const recordKey = record => `${record.conversation?.id}-${record.occurred_at}`;

const closeDrawer = () => {
  close();
  emit('close');
};

const onOpenChange = value => {
  if (!value) closeDrawer();
};

const fetchDrilldown = () => {
  if (!props.metric || !props.assistantId) return;

  openDrilldown({
    assistantId: props.assistantId,
    metric: props.metric,
    range: props.range,
  });
};

watch(
  () => props.open,
  isDrawerOpen => {
    if (!isDrawerOpen) {
      close();
      return;
    }
    fetchDrilldown();
  },
  { immediate: true }
);

watch(
  () => [props.metric, props.range],
  () => {
    if (props.open) fetchDrilldown();
  }
);

// The drawer's headline value is a snapshot of the previous assistant's card,
// so switching assistants (e.g. browser Back/Forward) closes it instead of
// refetching records that would no longer match the header.
watch(
  () => props.assistantId,
  () => {
    if (props.open) closeDrawer();
  }
);
</script>

<template>
  <Sheet :open="open" @update:open="onOpenChange">
    <SheetContent class="w-full gap-0 p-0 sm:max-w-xl" data-drilldown-drawer>
      <SheetHeader
        class="gap-1 border-0 border-b border-solid border-polaris-border px-6 py-5 pe-12"
      >
        <SheetTitle class="truncate">{{ title }}</SheetTitle>
        <p
          v-if="metricValue"
          class="tabular-nums type-polaris-page-title text-polaris-foreground"
        >
          {{ metricValue }}
        </p>
        <SheetDescription>{{ subtitle }}</SheetDescription>
      </SheetHeader>

      <div class="min-h-0 flex-1 overflow-y-auto px-5 py-3">
        <div
          v-if="isFetching"
          class="flex h-40 items-center justify-center text-polaris-muted-foreground"
        >
          <Spinner />
        </div>

        <p
          v-else-if="hasError"
          class="flex h-40 items-center justify-center type-polaris-body text-polaris-destructive"
        >
          {{ $t('POLARIS.OVERVIEW.DRILLDOWN.ERROR') }}
        </p>

        <p
          v-else-if="!hasRecords"
          class="flex h-40 items-center justify-center type-polaris-body text-polaris-muted-foreground"
        >
          {{ $t('POLARIS.OVERVIEW.DRILLDOWN.EMPTY') }}
        </p>

        <div v-else class="flex flex-col gap-2">
          <ReportDrilldownCard
            v-for="record in records"
            :key="recordKey(record)"
            :record="record"
          />

          <Button
            v-if="hasMore"
            variant="secondary"
            size="sm"
            class="mx-auto mt-2"
            :disabled="isFetchingMore"
            @click="loadMore"
          >
            <Spinner v-if="isFetchingMore" class="size-4" />
            {{ $t('POLARIS.OVERVIEW.DRILLDOWN.LOAD_MORE') }}
          </Button>
        </div>
      </div>
    </SheetContent>
  </Sheet>
</template>
